import React, { useMemo } from "react";
import { DataTable } from "@/components/ui/DataTable/index";
import { type DataTableColumn } from "@/components/ui/DataTable/index";
import moment from "moment-timezone";
import {
  useListDestinationHours,
  useListDestinationLocations,
  DestinationDailyHoursImproved,
  GroupedHoursResponse,
} from "@/lib/api/destinationHoursImproved";

interface HoursListViewProps {
  dateRange: { from?: Date; to?: Date };
}

interface FlattenedHour extends DestinationDailyHoursImproved {
  location_name: string;
  location_link: string;
  service_category_name: string;
}

export function HoursListView({ dateRange }: HoursListViewProps) {
  const locationsQuery = useListDestinationLocations();

  // Build location_ids string for API call
  const locationIds = useMemo(() => {
    if (!locationsQuery.data?.data) return "";
    return locationsQuery.data.data.map((loc: { id: string }) => loc.id).join(",");
  }, [locationsQuery.data]);

  const hoursQuery = useListDestinationHours({
    location_ids: locationIds,
    start_date: dateRange.from ? moment(dateRange.from).format("YYYY-MM-DD") : undefined,
    end_date: dateRange.to ? moment(dateRange.to).format("YYYY-MM-DD") : undefined,
    limit: 1000,
  });

  // Flatten grouped hours data for table display
  const flattenedHours = useMemo((): FlattenedHour[] => {
    if (!hoursQuery.data?.data) return [];

    const grouped = hoursQuery.data.data as GroupedHoursResponse;
    const flattened: FlattenedHour[] = [];

    Object.entries(grouped).forEach(([locationId, locationData]) => {
      Object.entries(locationData.service_categories).forEach(([serviceCategoryId, categoryData]) => {
        categoryData.hours.forEach(hour => {
          flattened.push({
            ...hour,
            location_name: locationData.location.name,
            location_link: locationData.location.link,
            service_category_name: categoryData.service_category?.service_name || "No Category",
          });
        });
      });
    });

    return flattened.sort((a, b) => {
      // Sort by location name, then service category, then date
      if (a.location_name !== b.location_name) {
        return a.location_name.localeCompare(b.location_name);
      }
      if (a.service_category_name !== b.service_category_name) {
        return a.service_category_name.localeCompare(b.service_category_name);
      }
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });
  }, [hoursQuery.data]);

  const columns = useMemo((): DataTableColumn<FlattenedHour>[] => [
    {
      id: "location_name",
      accessorKey: "location_name",
      header: "Location",
      cell: (info) => {
        const locationName = info.getValue() as string;
        const locationLink = (info.row.original as FlattenedHour).location_link;
        return (
          <a
            href={locationLink}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 hover:underline"
          >
            {locationName}
          </a>
        );
      },
      minSize: 150,
      maxSize: 200,
      enableSorting: true,
      enableGrouping: true,
    },
    {
      id: "service_category_name",
      accessorKey: "service_category_name",
      header: "Service Category",
      cell: (info) => info.getValue() as string,
      minSize: 120,
      maxSize: 180,
      enableSorting: true,
      enableGrouping: true,
    },
    {
      id: "date",
      accessorKey: "date",
      header: "Date",
      cell: (info) => moment(info.getValue() as string).format("MMM DD, YYYY"),
      minSize: 110,
      maxSize: 130,
      enableSorting: true,
    },
    {
      id: "status",
      accessorKey: "status",
      header: "Status",
      cell: (info) => info.getValue() as string,
      minSize: 100,
      maxSize: 120,
      enableSorting: true,
    },
    {
      id: "open_time",
      accessorKey: "open_time",
      header: "Open Time",
      cell: (info) => {
        const timeStr = info.getValue() as string;
        if (!timeStr) return "--";
        return moment(timeStr, "HH:mm").format("hh:mm a");
      },
      minSize: 120,
      maxSize: 120,
      enableSorting: false,
      enableFiltering: false,
    },
    {
      id: "close_time",
      accessorKey: "close_time",
      header: "Close Time",
      cell: (info) => {
        const timeStr = info.getValue() as string;
        if (!timeStr) return "--";
        return moment(timeStr, "HH:mm").format("hh:mm a");
      },
      minSize: 120,
      maxSize: 120,
      enableSorting: false,
      enableFiltering: false,
    },
    {
      id: "instructions_note",
      accessorKey: "instructions_note",
      header: "Instructions Note",
      cell: (info) => (info.getValue() as string) || "",
      minSize: 120,
      maxSize: 180,
      enableSorting: false,
      enableFiltering: false,
      enableTooltip: true,
    },
    {
      id: "global_note",
      accessorKey: "global_note",
      header: "Global Note",
      cell: (info) => (info.getValue() as string) || "",
      minSize: 120,
      maxSize: 180,
      enableSorting: false,
      enableFiltering: false,
      enableTooltip: true,
    },
    {
      id: "grade_note",
      accessorKey: "grade_note",
      header: "Grade Note",
      cell: (info) => (info.getValue() as string) || "",
      minSize: 120,
      maxSize: 180,
      enableSorting: false,
      enableFiltering: false,
      enableTooltip: true,
    },
    {
      id: "other_note",
      accessorKey: "other_note",
      header: "Other Note",
      cell: (info) => (info.getValue() as string) || "",
      minSize: 120,
      maxSize: 180,
      enableFiltering: false,
      enableSorting: false,
      enableTooltip: true,
    },
    {
      id: "closure_reason",
      accessorKey: "closure_reason",
      header: "Closure Reason",
      cell: (info) => (info.getValue() as string) || "",
      minSize: 150,
      maxSize: 200,
      enableSorting: false,
      enableFiltering: false,
      enableTooltip: true
    },
  ], []);

  const isLoading = locationsQuery.isLoading || hoursQuery.isLoading;

  return (
    <div className="space-y-4">
      <DataTable
        className="shadow-lg"
        stickyHeaders
        tableClassName="max-h-[calc(100vh-300px)] overflow-y-scroll"
        data={flattenedHours}
        columns={columns}
        enableSorting
        enableMultiSort
        enableFiltering
        enableGrouping
        enableColumnVisibility
        enableExport
        filterStyle="select"
        filterMode="internal"
        groupBy={["location_name", "service_category_name"]}
        emptyMessage={
          !locationIds
            ? "Loading locations..."
            : isLoading
            ? "Loading hours..."
            : "No destination hours found for the selected date range"
        }
      />
    </div>
  );
}
